import { useEffect, useState } from "react";
import api from "../axios";
import {
  Image as ImageIcon,
  Send,
  RefreshCw,
  Globe,
  Camera,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ThumbsUp,
  MessageCircle,
  Eye,
  Sparkles,
  Package,
  Gift,
  Lightbulb,
  Star,
} from "lucide-react";

const MEDIA_BASE = (process.env.REACT_APP_API_URL || "http://localhost:5000/api").replace(
  /\/api\/?$/,
  ""
);

const STATUS_STYLES = {
  success: { color: "bg-green-100 text-green-700", icon: CheckCircle2 },
  partial: { color: "bg-amber-100 text-amber-700", icon: AlertTriangle },
  failed: { color: "bg-red-100 text-red-700", icon: XCircle },
};

const POST_TYPE_META = {
  offer: { label: "Offer", icon: Gift, color: "bg-purple-100 text-purple-700" },
  tip: { label: "Tip / Educational", icon: Lightbulb, color: "bg-blue-100 text-blue-700" },
  testimonial: { label: "Trust / Social Proof", icon: Star, color: "bg-amber-100 text-amber-700" },
  product_spotlight: { label: "Product Spotlight", icon: Package, color: "bg-teal-100 text-teal-700" },
};

function PageCard({ page, onChanged }) {
  const [form, setForm] = useState({
    business_category: page.business_category || "",
    frequency_days: page.frequency_days || 3,
    platforms: page.platforms ? page.platforms.split(",") : ["facebook", "instagram"],
    status: page.plan_status || "active",
    custom_prompt: page.custom_prompt || "",
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(page.logo_url ? `${MEDIA_BASE}${page.logo_url}` : null);
  const [saving, setSaving] = useState(false);
  const [posting, setPosting] = useState(false);
  const [msg, setMsg] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);

  const togglePlatform = (p) => {
    setForm((prev) => ({
      ...prev,
      platforms: prev.platforms.includes(p) ? prev.platforms.filter((x) => x !== p) : [...prev.platforms, p],
    }));
  };

  const handleLogoChange = (e) => {
    const f = e.target.files?.[0];
    setLogoFile(f || null);
    setLogoPreview(f ? URL.createObjectURL(f) : logoPreview);
  };

  const savePlan = async (e) => {
    e.preventDefault();
    setMsg("");

    if (!form.business_category.trim()) {
      setMsg("Business category likhna zaroori hai.");
      return;
    }
    if (!form.platforms.length) {
      setMsg("Kam se kam ek platform select karein.");
      return;
    }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("business_category", form.business_category);
      fd.append("frequency_days", form.frequency_days);
      fd.append("status", form.status);
      fd.append("custom_prompt", form.custom_prompt || "");
      form.platforms.forEach((p) => fd.append("platforms", p));
      if (logoFile) fd.append("logo", logoFile);

      await api.put(`/poster/plan/${page.social_page_id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMsg("Plan save ho gaya ✅");
      setLogoFile(null);
      onChanged();
    } catch (err) {
      setMsg(err.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get("/poster/history", { params: { social_page_id: page.social_page_id } });
      setHistory(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setHistoryLoading(false);
    }
  };

  const postNow = async () => {
    setPosting(true);
    setMsg("");
    try {
      const res = await api.post(`/poster/plan/${page.social_page_id}/post-now`);
      setMsg(res.data.message || "Processing...");
      [15000, 30000, 60000, 90000, 120000].forEach((delay) => setTimeout(loadHistory, delay));
    } catch (err) {
      setMsg(err.response?.data?.message || "Post now failed");
    } finally {
      setPosting(false);
    }
  };

  const refreshEngagement = async (postId) => {
    setRefreshingId(postId);
    try {
      await api.post(`/poster/history/${postId}/refresh-engagement`);
      loadHistory();
    } catch (e) {
      alert(e.response?.data?.message || "Refresh failed");
    } finally {
      setRefreshingId(null);
    }
  };

  useEffect(() => {
    if (expanded && !history.length) loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden mb-6">
      <div className="p-5 flex items-center justify-between flex-wrap gap-3 bg-gray-50 border-b">
        <div>
          <div className="font-bold text-gray-900 flex items-center gap-2">
            <Globe size={16} className="text-blue-600" /> {page.page_name}
            {page.instagram_username && (
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <Camera size={13} className="text-pink-600" /> @{page.instagram_username}
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {page.plan_id ? (
              <>
                Har {page.frequency_days} din • Status: {form.status} • Next post:{" "}
                {page.next_post_at ? new Date(page.next_post_at).toLocaleString() : "-"}
              </>
            ) : (
              "Abhi tak koi plan set nahi hai"
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={postNow}
            disabled={posting || !page.plan_id}
            className="text-xs font-semibold bg-green-600 text-white px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50"
          >
            <Send size={13} /> {posting ? "Processing..." : "Post Now"}
          </button>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-gray-600 border px-2.5 py-1.5 rounded-lg"
          >
            {expanded ? "Hide" : "Manage"}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="p-5 space-y-6">
          <form onSubmit={savePlan} className="grid md:grid-cols-2 gap-x-6 gap-y-4">
            <div className="md:col-span-2">
              <label className="text-sm font-semibold text-gray-700">
                Business Category <span className="text-red-500">*</span>
              </label>
              <input
                value={form.business_category}
                onChange={(e) => setForm({ ...form, business_category: e.target.value })}
                placeholder="e.g. Hair Clinic, Bakery, IVF Center"
                className="border border-gray-300 rounded-lg px-3 py-2.5 w-full mt-1.5 focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700">Post Frequency (din ka gap)</label>
              <input
                type="number"
                min={1}
                value={form.frequency_days}
                onChange={(e) => setForm({ ...form, frequency_days: e.target.value })}
                className="border border-gray-300 rounded-lg px-3 py-2.5 w-full mt-1.5 focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700">Plan Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="border border-gray-300 rounded-lg px-3 py-2.5 w-full mt-1.5 focus:ring-2 focus:ring-purple-500 outline-none"
              >
                <option value="active">Active (auto-posting on)</option>
                <option value="paused">Paused</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-semibold text-gray-700">Custom Prompt / Instructions (optional)</label>
              <textarea
                rows={3}
                value={form.custom_prompt}
                onChange={(e) => setForm({ ...form, custom_prompt: e.target.value })}
                placeholder="e.g. hamesha navy-gold color theme use karna, phone number 8700860292 dikhana"
                className="border border-gray-300 rounded-lg px-3 py-2.5 w-full mt-1.5 focus:ring-2 focus:ring-purple-500 outline-none"
              />
              <p className="text-xs text-gray-400 mt-1">
                AI khud alternate karta hai: Offer → Tip → Product Spotlight → Trust post, aur upcoming festivals ko bhi automatically include karta hai.
              </p>
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-semibold text-gray-700">Logo / Reference Image (optional)</label>
              <div className="flex items-center gap-4 mt-1.5">
                <label className="flex items-center gap-2 border-2 border-dashed border-gray-300 rounded-lg px-4 py-3 cursor-pointer text-sm text-gray-600 hover:border-purple-400 hover:text-purple-600 transition">
                  <ImageIcon size={16} />
                  {logoFile ? logoFile.name : "Logo/Image upload karein"}
                  <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleLogoChange} className="hidden" />
                </label>
                {logoPreview && <img src={logoPreview} alt="logo preview" className="w-16 h-16 rounded-lg object-cover border" />}
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-semibold text-gray-700">Platforms</label>
              <div className="flex gap-3 mt-1.5">
                <button
                  type="button"
                  onClick={() => togglePlatform("facebook")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-semibold ${
                    form.platforms.includes("facebook") ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-300"
                  }`}
                >
                  <Globe size={16} /> Facebook
                </button>
                <button
                  type="button"
                  onClick={() => togglePlatform("instagram")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-semibold ${
                    form.platforms.includes("instagram") ? "bg-pink-600 text-white border-pink-600" : "bg-white text-gray-600 border-gray-300"
                  }`}
                >
                  <Camera size={16} /> Instagram
                </button>
              </div>
            </div>

            {msg && (
              <div
                className={`md:col-span-2 text-sm rounded-lg px-3 py-2.5 border ${
                  msg.includes("✅") ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-600 border-red-200"
                }`}
              >
                {msg}
              </div>
            )}

            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold px-6 py-2.5 rounded-lg shadow hover:opacity-90 disabled:opacity-50"
              >
                <Sparkles size={16} /> {saving ? "Saving..." : "Save Plan"}
              </button>
            </div>
          </form>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-gray-900 text-sm">History & Performance</h3>
              <button onClick={loadHistory} className="text-xs text-gray-500 flex items-center gap-1">
                <RefreshCw size={12} /> Refresh
              </button>
            </div>

            {historyLoading ? (
              <p className="text-gray-500 text-xs">Loading...</p>
            ) : !history.length ? (
              <p className="text-gray-500 text-xs">Abhi tak koi post nahi hui is page ke liye.</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-3">
                {history.map((h) => {
                  const vStyle = STATUS_STYLES[h.status] || STATUS_STYLES.failed;
                  const typeMeta = POST_TYPE_META[h.post_type] || null;
                  return (
                    <div key={h.id} className="border rounded-xl overflow-hidden">
                      {h.image_url && <img src={`${MEDIA_BASE}${h.image_url}`} alt="" className="w-full h-40 object-cover" />}
                      <div className="p-3">
                        <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 ${vStyle.color}`}>
                            <vStyle.icon size={11} /> {h.status}
                          </span>
                          {typeMeta && (
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 ${typeMeta.color}`}>
                              <typeMeta.icon size={11} /> {typeMeta.label}
                            </span>
                          )}
                          {h.festival_name && (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">
                              🎉 {h.festival_name}
                            </span>
                          )}
                        </div>
                        {h.product_name && (
                          <p className="text-[11px] text-teal-700 font-semibold mb-1">Featured: {h.product_name}</p>
                        )}
                        <p className="text-xs text-gray-800 line-clamp-3">{h.caption}</p>
                        <p className="text-[10px] text-gray-400 mt-1">{new Date(h.posted_at).toLocaleString()}</p>

                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-3 text-[11px] text-gray-600">
                            <span className="flex items-center gap-1"><ThumbsUp size={11} /> {h.likes_count ?? "-"}</span>
                            <span className="flex items-center gap-1"><MessageCircle size={11} /> {h.comments_count ?? "-"}</span>
                            {h.reach !== null && h.reach !== undefined && (
                              <span className="flex items-center gap-1"><Eye size={11} /> {h.reach}</span>
                            )}
                          </div>
                          {(h.fb_post_id || h.ig_post_id) && (
                            <button
                              onClick={() => refreshEngagement(h.id)}
                              disabled={refreshingId === h.id}
                              className="text-[10px] text-purple-600 flex items-center gap-1 disabled:opacity-50"
                            >
                              <RefreshCw size={10} /> {refreshingId === h.id ? "..." : "Check"}
                            </button>
                          )}
                        </div>
                        {h.error_message && <p className="text-[10px] text-red-500 mt-1">{h.error_message}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PosterPlanner() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPages = async () => {
    setLoading(true);
    try {
      const res = await api.get("/poster/pages");
      setPages(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPages();
  }, []);

  return (
    <div className="w-full min-h-full bg-gray-50 px-4 py-6 md:px-8 md:py-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ImageIcon className="text-purple-600" size={24} />
            AI Poster Planner
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Har connected Facebook/Instagram Page ka apna alag content plan — AI automatically Offer, Tip, Product Spotlight aur Trust posts rotate karta hai, aur upcoming festivals ko bhi include karta hai.
          </p>
        </div>

        {loading ? (
          <p className="text-gray-500 text-sm">Loading...</p>
        ) : !pages.length ? (
          <p className="text-gray-500 text-sm">
            Koi connected Facebook/Instagram Page nahi mili. Admin Panel se pehle "Social Media Pages" connect karein.
          </p>
        ) : (
          <div className="pb-8">
            {pages.map((page) => (
              <PageCard key={page.social_page_id} page={page} onChanged={loadPages} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}